import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { BadgeCheck, RefreshCcw, Play, XCircle } from "lucide-react";

const PRIORITY_COLOR = {
  high: "border-rose-300 text-rose-800 bg-rose-50",
  normal: "border-slate-300 text-slate-700 bg-slate-50",
  low: "border-emerald-300 text-emerald-800 bg-emerald-50",
};
const STATUS_PILL = {
  assigned: "bg-amber-100 text-amber-800 border-amber-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function PromoterMyTasks() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/promoter/tasks`).then(({ data }) => setItems(data.items || [])), []);
  useEffect(() => { load(); }, [load]);

  const start = async (t) => {
    await api.post(`/promoter/tasks/${t.id}/status`, { status: "in_progress" });
    toast.success("Marked as in progress.");
    load();
  };
  const fail = async (t) => {
    const notes = window.prompt("Quick note for why this could not be completed (optional):", "");
    if (notes === null) return;
    await api.post(`/promoter/tasks/${t.id}/status`, { status: "failed", notes });
    toast.success("Marked as failed.");
    load();
  };

  return (
    <div data-testid="promoter-my-tasks-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <BadgeCheck className="w-5 h-5" /> Assigned Visits
        </h1>
        <button onClick={load} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Visits the admin has asked you to make. Tap a card to log your visit and auto-complete the task.
      </p>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((t) => (
          <article key={t.id} data-testid={`my-task-${t.id}`}
                    className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{t.due_date ? `Due ${t.due_date}` : "No due date"}</div>
                <h3 className="font-display text-lg font-semibold mt-0.5 truncate">{t.business_name}</h3>
              </div>
              <span className={`px-1.5 py-0.5 text-[10px] rounded border uppercase ${PRIORITY_COLOR[t.priority] || "bg-slate-100 border-slate-200"}`}>{t.priority}</span>
            </div>
            <div className="mt-1 text-xs text-slate-600">{t.business_address}</div>
            {t.contact_name && <div className="text-xs text-slate-600 mt-0.5">Contact: {t.contact_name} {t.contact_info ? `· ${t.contact_info}` : ""}</div>}
            {t.reason && <div className="mt-2 text-xs text-slate-700"><strong>Reason:</strong> {t.reason}</div>}
            {t.instructions && <div className="mt-1 text-xs text-slate-700"><strong>Instructions:</strong> {t.instructions}</div>}
            <div className="mt-3 flex items-center gap-2">
              <span className={`px-1.5 py-0.5 text-[10px] rounded border uppercase ${STATUS_PILL[t.status]}`}>{t.status}</span>
              <Link to={`/promoter/new-log?task_id=${t.id}`}
                     data-testid={`task-submit-${t.id}`}
                     className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[#1B4332] text-white">
                Log visit & complete
              </Link>
            </div>
            {t.status === "assigned" && (
              <div className="mt-2 flex items-center gap-1">
                <button onClick={() => start(t)} data-testid={`task-start-${t.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-300">
                  <Play className="w-3 h-3" /> Mark in-progress
                </button>
                <button onClick={() => fail(t)} data-testid={`task-fail-${t.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-rose-300 text-rose-700">
                  <XCircle className="w-3 h-3" /> Could not complete
                </button>
              </div>
            )}
            {t.promoter_status_notes && (
              <div className="mt-2 text-[11px] text-slate-500 italic">Your note: {t.promoter_status_notes}</div>
            )}
          </article>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">No assigned visits right now.</div>}
      </div>
    </div>
  );
}
